
import { Link } from 'react-router-dom';

interface ExperienceCardProps {
  experience: {
    id: number;
    title: string;
    location: string;
    description: string;
    image: string;
    activities?: string[];
    duration: string;
    difficulty: string;
    price: string;
    slug: string;
  };
}

export default function ExperienceCard({ experience }: ExperienceCardProps) {
  // Map experience slugs to actual routes
  const getExperienceRoute = (slug: string) => {
    const routeMap: { [key: string]: string } = {
      'mountain': '/experiences/mountain',
      'forest': '/experiences/forest',
      'lakeside': '/experiences/lakeside',
      'desert': '/experiences/desert'
    };
    return routeMap[slug] || '/experiences';
  };

  // Get booking route with experience context
  const getBookingRoute = (slug: string) => {
    return `/book?experience=${slug}`;
  };

  // Safely handle activities array
  const activities = experience.activities || [];

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
      <div className="relative h-64 overflow-hidden">
        <img 
          src={experience.image} 
          alt={experience.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-4 left-4 bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-medium">
          {experience.difficulty}
        </div>
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 cursor-pointer hover:bg-white transition-colors">
          <i className="ri-heart-line text-gray-700 text-lg"></i>
        </div>
      </div>
      
      <div className="p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-bold text-gray-900">{experience.title}</h3>
          <span className="text-emerald-600 font-semibold">{experience.price}</span>
        </div>
        
        <div className="flex items-center text-gray-600 mb-3">
          <i className="ri-map-pin-line text-emerald-600 mr-2"></i>
          <span className="text-sm">{experience.location}</span>
        </div>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{experience.description}</p>

        <div className="flex items-center text-gray-500 text-sm mb-4">
          <i className="ri-time-line mr-2"></i>
          <span>{experience.duration}</span>
        </div>

        {activities.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {activities.slice(0, 3).map((activity, index) => (
              <span key={index} className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-medium">
                {activity}
              </span>
            ))}
            {activities.length > 3 && (
              <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs">
                +{activities.length - 3} more
              </span>
            )}
          </div>
        )}

        <div className="flex gap-3">
          <Link 
            to={getExperienceRoute(experience.slug)}
            className="flex-1 bg-emerald-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-emerald-700 transition-colors text-center whitespace-nowrap cursor-pointer"
          >
            Learn More
          </Link>
          <Link 
            to={getBookingRoute(experience.slug)}
            className="flex-1 bg-gray-900 text-white py-2 px-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors text-center whitespace-nowrap cursor-pointer"
          >
            Book Now
          </Link>
        </div>
      </div>
    </div>
  );
}
